import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import moment from "moment";
import Links from "../../Data/Links";
import { Bar, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";
import "./PaidMediaCombinedPerformance.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const toTargetNumber = (value) => {
  if (value === null || value === undefined || value === "") return undefined;
  const num = Number(value);
  return Number.isFinite(num) ? num : undefined;
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatNumberOne = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return num.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
};

const normalizeStoreKey = (value) => String(value || "").trim().toLowerCase();

const getStoreName = (row) => {
  return (
    row?.store_name ||
    row?.STORE_NAME ||
    row?.store ||
    row?.dealership ||
    row?.name ||
    row?.storeName ||
    "-"
  );
};

const getDivisionLabel = (row) => {
  return (
    row?.division ||
    row?.store_division ||
    row?.storeDivision ||
    row?.Division ||
    ""
  ).trim();
};

const usedDivisions = ["DriveNation", "FFUN Cars", "Used", "Carooga", "FFUN Group", "Group"];
const usedStoreNames = ["Carooga", "FFUN Group"];

const normalizeDivision = (divisionValue) => {
  const division = (divisionValue || "").trim();
  if (!division) return "";
  if (usedDivisions.includes(division)) return "Used";
  return "OEM";
};

const getDivisionGroup = (row) => {
  const label = getDivisionLabel(row);
  if (label.toLowerCase() === "group") return "Group";
  const normalized = normalizeDivision(label);
  if (normalized) return normalized;

  const storeName = getStoreName(row);
  if (storeName) {
    const normalizedStore = storeName.trim().toLowerCase();
    const isUsedStore = usedStoreNames.some(
      (name) => name.toLowerCase() === normalizedStore || normalizedStore.includes(name.toLowerCase())
    );
    if (isUsedStore) return "Used";
  }

  return "OEM";
};

const getTargetsDivision = (row) => {
  const label = getDivisionLabel(row);
  if (label.toLowerCase() === "group") return "Used";
  const normalized = normalizeDivision(label);
  return normalized || "OEM";
};

const COLUMNS = [
  { key: "name", label: "Dealership" },
  { key: "gadsSpend", label: "Spend" },
  { key: "gadsLeads", label: "Leads" },
  { key: "gadsCpl", label: "CPL" },
  { key: "metaSpend", label: "Spend" },
  { key: "metaLeads", label: "Leads" },
  { key: "metaCpl", label: "CPL" },
  { key: "combinedSpend", label: "Spend" },
  { key: "combinedLeads", label: "Leads" },
  { key: "combinedCpl", label: "CPL" }
];

const GADS_COLUMNS = ["gadsSpend", "gadsLeads", "gadsCpl"];
const META_COLUMNS = ["metaSpend", "metaLeads", "metaCpl"];
const COMBINED_COLUMNS = ["combinedSpend", "combinedLeads", "combinedCpl"];

export default function PaidMediaCombinedPerformance() {
  const [googleRows, setGoogleRows] = useState([]);
  const [metaRows, setMetaRows] = useState([]);
  const [targetsByStore, setTargetsByStore] = useState(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedStores, setSelectedStores] = useState(new Set());
  const [storeMenuOpen, setStoreMenuOpen] = useState(false);
  const [cplView, setCplView] = useState("combined");
  const [trendGroup, setTrendGroup] = useState("all");
  const [momView, setMomView] = useState("combined");
  const [monthlySeries, setMonthlySeries] = useState([]);
  const storeMenuRef = useRef(null);
  const hasInitializedStores = useRef(false);

  const startDate = useMemo(() => moment().startOf("month"), []);
  const endDate = useMemo(() => {
    const yesterday = moment().subtract(1, "day");
    return yesterday.isBefore(startDate) ? startDate.clone() : yesterday;
  }, [startDate]);

  const dateLabel = `${startDate.format("MMM D")} - ${endDate.format("D, YYYY")}`;
  const targetMonth = startDate.format("YYYY-MM");

  const fetchRows = () => {
    setLoading(true);
    setError("");

    const payload = {
      start_date: startDate.format("YYYY-MM-DD"),
      end_date: endDate.format("YYYY-MM-DD")
    };

    Promise.allSettled([
      axios.post(Links.googleAdsDivisionsDaily, payload),
      axios.post(Links.metaAdsDivisionsDaily, payload)
    ])
      .then(([gadsResult, metaResult]) => {
        const gadsList = gadsResult.status === "fulfilled" ? gadsResult.value?.data?.data || gadsResult.value?.data || [] : [];
        const metaList = metaResult.status === "fulfilled" ? metaResult.value?.data?.data || metaResult.value?.data || [] : [];
        setGoogleRows(Array.isArray(gadsList) ? gadsList : []);
        setMetaRows(Array.isArray(metaList) ? metaList : []);
        if (gadsResult.status === "rejected" && metaResult.status === "rejected") {
          setError("Failed to load combined performance data.");
        }
      })
      .catch((err) => {
        console.log("Paid media combined fetch error:", err);
        setGoogleRows([]);
        setMetaRows([]);
        setError("Failed to load combined performance data.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchRows();
  }, []);

  useEffect(() => {
    const fetchMonthlySeries = async () => {
      const months = Array.from({ length: 12 }, (_, index) => {
        const monthStart = startDate.clone().subtract(11 - index, "months").startOf("month");
        const monthEnd = monthStart.isSame(startDate, "month")
          ? endDate.clone()
          : monthStart.clone().endOf("month");
        return {
          key: monthStart.format("YYYY-MM"),
          label: monthStart.format("MMM"),
          start: monthStart.format("YYYY-MM-DD"),
          end: monthEnd.format("YYYY-MM-DD")
        };
      });

      try {
        const results = await Promise.all(
          months.map(async (month) => {
            const payload = { start_date: month.start, end_date: month.end };
            const [gadsResult, metaResult] = await Promise.all([
              axios.post(Links.googleAdsDivisionsDaily, payload),
              axios.post(Links.metaAdsDivisionsDaily, payload)
            ]);

            const gadsList = gadsResult?.data?.data || gadsResult?.data || [];
            const metaList = metaResult?.data?.data || metaResult?.data || [];

            const stores = new Map();

            const ensureEntry = (row) => {
              const store = getStoreName(row);
              if (!store || store === "-") return null;
              if (!stores.has(store)) {
                stores.set(store, {
                  store,
                  divisionGroup: getDivisionGroup(row),
                  gadsSpend: 0,
                  gadsLeads: 0,
                  metaSpend: 0,
                  metaLeads: 0
                });
              }
              return stores.get(store);
            };

            gadsList.forEach((row) => {
              const entry = ensureEntry(row);
              if (!entry) return;
              entry.gadsSpend += toNumber(row?.spend ?? row?.cost ?? row?.amount_spent);
              entry.gadsLeads += toNumber(row?.leads ?? row?.LEADS);
            });

            metaList.forEach((row) => {
              const entry = ensureEntry(row);
              if (!entry) return;
              entry.metaSpend += toNumber(row?.spend ?? row?.cost ?? row?.amount_spent);
              entry.metaLeads += toNumber(row?.leads ?? row?.LEADS);
            });

            return { ...month, stores };
          })
        );
        setMonthlySeries(results);
      } catch (err) {
        console.log("Monthly series fetch error:", err);
        setMonthlySeries([]);
      }
    };

    fetchMonthlySeries();
  }, [startDate, endDate]);

  useEffect(() => {
    const combined = [...googleRows, ...metaRows];
    if (combined.length === 0) {
      setTargetsByStore(new Map());
      return;
    }

    const storeDivisionPairs = new Map();
    combined.forEach((row) => {
      const storeName = getStoreName(row);
      if (!storeName || storeName === "-") return;
      const storeKey = normalizeStoreKey(storeName);
      if (!storeDivisionPairs.has(storeKey)) {
        storeDivisionPairs.set(storeKey, {
          name: storeName,
          division: getTargetsDivision(row)
        });
      }
    });

    const entries = Array.from(storeDivisionPairs.entries());
    if (entries.length === 0) {
      setTargetsByStore(new Map());
      return;
    }

    Promise.allSettled(
      entries.map(([, meta]) =>
        axios.post(Links.marketingTargets, {
          division: meta.division,
          dealer: meta.name,
          month: targetMonth
        })
      )
    )
      .then((results) => {
        const next = new Map();
        results.forEach((result, index) => {
          if (result.status !== "fulfilled") return;
          const storeKey = entries[index][0];
          const payload = result.value?.data?.data ?? result.value?.data ?? {};
          const targetRow = Array.isArray(payload) ? payload[0] : payload;
          if (!targetRow) return;

          const gadsBudget = toTargetNumber(
            targetRow?.google_ads_budget || targetRow?.budget || targetRow?.gads_budget
          );
          const gadsLeadGoal = toTargetNumber(
            targetRow?.google_ads_leads_goal || targetRow?.leads_goal || targetRow?.gads_leads_goal
          );
          const metaBudget = toTargetNumber(
            targetRow?.meta_ads_budget || targetRow?.fb_ads_budget || targetRow?.meta_budget
          );
          const metaLeadGoal = toTargetNumber(
            targetRow?.meta_ads_leads_goal || targetRow?.fb_ads_leads_goal || targetRow?.meta_leads_goal
          );

          if (
            gadsBudget !== undefined ||
            gadsLeadGoal !== undefined ||
            metaBudget !== undefined ||
            metaLeadGoal !== undefined
          ) {
            next.set(storeKey, { gadsBudget, gadsLeadGoal, metaBudget, metaLeadGoal });
          }
        });
        setTargetsByStore(next);
      })
      .catch((err) => {
        console.log("Paid media combined targets error:", err);
        setTargetsByStore(new Map());
      });
  }, [googleRows, metaRows, targetMonth]);

  const aggregatedRows = useMemo(() => {
    const map = new Map();

    const ensureEntry = (store, row) => {
      if (!map.has(store)) {
        map.set(store, {
          store,
          divisionGroup: getDivisionGroup(row),
          gadsSpend: 0,
          gadsLeads: 0,
          gadsBudget: 0,
          gadsLeadGoal: 0,
          metaSpend: 0,
          metaLeads: 0,
          metaBudget: 0,
          metaLeadGoal: 0
        });
      }
      return map.get(store);
    };

    googleRows.forEach((row) => {
      const store = getStoreName(row);
      if (!store || store === "-") return;
      const entry = ensureEntry(store, row);
      entry.gadsSpend += toNumber(row?.spend ?? row?.cost ?? row?.amount_spent);
      entry.gadsLeads += toNumber(row?.leads ?? row?.LEADS);
    });

    metaRows.forEach((row) => {
      const store = getStoreName(row);
      if (!store || store === "-") return;
      const entry = ensureEntry(store, row);
      entry.metaSpend += toNumber(row?.spend ?? row?.cost ?? row?.amount_spent);
      entry.metaLeads += toNumber(row?.leads ?? row?.LEADS);
    });

    const withTargets = Array.from(map.values()).map((entry) => {
      const targets = targetsByStore.get(normalizeStoreKey(entry.store));
      if (targets) {
        if (targets.gadsBudget !== undefined) entry.gadsBudget = targets.gadsBudget;
        if (targets.gadsLeadGoal !== undefined) entry.gadsLeadGoal = targets.gadsLeadGoal;
        if (targets.metaBudget !== undefined) entry.metaBudget = targets.metaBudget;
        if (targets.metaLeadGoal !== undefined) entry.metaLeadGoal = targets.metaLeadGoal;
      }
      return entry;
    });

    return withTargets.map((entry) => {
      const gadsCpl = entry.gadsLeads ? entry.gadsSpend / entry.gadsLeads : 0;
      const metaCpl = entry.metaLeads ? entry.metaSpend / entry.metaLeads : 0;
      const combinedBudget = entry.gadsBudget + entry.metaBudget;
      const combinedSpend = entry.gadsSpend + entry.metaSpend;
      const combinedLeads = entry.gadsLeads + entry.metaLeads;
      const combinedCpl = combinedLeads ? combinedSpend / combinedLeads : 0;
      return {
        ...entry,
        gadsCpl,
        metaCpl,
        combinedBudget,
        combinedSpend,
        combinedLeads,
        combinedCpl
      };
    });
  }, [googleRows, metaRows, targetsByStore]);

  useEffect(() => {
    if (hasInitializedStores.current || aggregatedRows.length === 0) return;
    setSelectedStores(new Set(aggregatedRows.map((row) => row.store)));
    hasInitializedStores.current = true;
  }, [aggregatedRows]);

  const filteredRows = useMemo(() => {
    if (selectedStores.size === 0) return [];
    return aggregatedRows.filter((row) => selectedStores.has(row.store));
  }, [aggregatedRows, selectedStores]);

  const calculateTotals = (items) => {
    const totals = items.reduce(
      (acc, row) => {
        acc.gadsSpend += row.gadsSpend;
        acc.gadsLeads += row.gadsLeads;
        acc.gadsBudget += row.gadsBudget;
        acc.gadsLeadGoal += row.gadsLeadGoal;
        acc.metaSpend += row.metaSpend;
        acc.metaLeads += row.metaLeads;
        acc.metaBudget += row.metaBudget;
        acc.metaLeadGoal += row.metaLeadGoal;
        acc.combinedBudget += row.combinedBudget;
        acc.combinedSpend += row.combinedSpend;
        acc.combinedLeads += row.combinedLeads;
        return acc;
      },
      {
        gadsSpend: 0,
        gadsLeads: 0,
        gadsBudget: 0,
        gadsLeadGoal: 0,
        metaSpend: 0,
        metaLeads: 0,
        metaBudget: 0,
        metaLeadGoal: 0,
        combinedBudget: 0,
        combinedSpend: 0,
        combinedLeads: 0
      }
    );

    const gadsCpl = totals.gadsLeads ? totals.gadsSpend / totals.gadsLeads : 0;
    const metaCpl = totals.metaLeads ? totals.metaSpend / totals.metaLeads : 0;
    const combinedCpl = totals.combinedLeads ? totals.combinedSpend / totals.combinedLeads : 0;

    return { ...totals, gadsCpl, metaCpl, combinedCpl };
  };

  const getCplClass = useCallback((current, goal) => {
    if (!goal) return "";
    if (current <= goal) return "cpl-green";
    if (current <= goal * 1.15) return "cpl-yellow";
    return "cpl-red";
  }, []);

  const getCplColor = useCallback(
    (current, goal) => {
      const cls = getCplClass(current, goal);
      if (cls === "cpl-green") return "#57a56d";
      if (cls === "cpl-yellow") return "#c79a2b";
      if (cls === "cpl-red") return "#f26a6f";
      return "#6f7f96";
    },
    [getCplClass]
  );

  const sections = useMemo(() => {
    const oemRows = filteredRows.filter((row) => row.divisionGroup === "OEM");
    const usedRows = filteredRows.filter((row) => row.divisionGroup === "Used");
    const groupRows = filteredRows.filter((row) => row.divisionGroup === "Group");
    return [
      { key: "OEM", label: "OEM", rows: oemRows },
      { key: "USED", label: "USED", rows: usedRows },
      { key: "GROUP", label: "GROUP", rows: groupRows }
    ];
  }, [filteredRows]);

  const leadsChartData = useMemo(() => {
    const labels = filteredRows.map((row) => row.store);
    const leadGoals = filteredRows.map((row) => row.gadsLeadGoal + row.metaLeadGoal || 0);
    const projected = filteredRows.map((row) => {
      if (!row.combinedCpl) return 0;
      return row.combinedBudget ? row.combinedBudget / row.combinedCpl : 0;
    });

    return {
      labels,
      datasets: [
        {
          label: "Lead Goal",
          data: leadGoals,
          backgroundColor: "#7a7a7a",
          borderRadius: 4,
          barPercentage: 0.75,
          categoryPercentage: 0.75
        },
        {
          label: "Projected",
          data: projected,
          backgroundColor: "#f6a043",
          borderRadius: 4,
          barPercentage: 0.75,
          categoryPercentage: 0.75
        }
      ]
    };
  }, [filteredRows]);

  const leadsChartOptions = useMemo(
    () => ({
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        datalabels: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.x.toFixed(1)}`
          }
        }
      },
      scales: {
        x: {
          grid: { color: "#f0f0f0" },
          ticks: { font: { family: "Rubik", size: 11 } }
        },
        y: {
          grid: { display: false },
          ticks: { font: { family: "Rubik", size: 11 } }
        }
      }
    }),
    []
  );

  const cplChartData = useMemo(() => {
    const labels = filteredRows.map((row) => row.store);

    const values = filteredRows.map((row) => {
      if (cplView === "google") return row.gadsCpl || 0;
      if (cplView === "meta") return row.metaCpl || 0;
      return row.combinedCpl || 0;
    });

    const colors = filteredRows.map((row) => {
      const gadsGoalCpl = row.gadsLeadGoal ? row.gadsBudget / row.gadsLeadGoal : 0;
      const metaGoalCpl = row.metaLeadGoal ? row.metaBudget / row.metaLeadGoal : 0;
      const combinedGoal = row.gadsLeadGoal + row.metaLeadGoal;
      const combinedGoalCpl = combinedGoal
        ? (row.gadsBudget + row.metaBudget) / combinedGoal
        : 0;

      if (cplView === "google") return getCplColor(row.gadsCpl, gadsGoalCpl);
      if (cplView === "meta") return getCplColor(row.metaCpl, metaGoalCpl);
      return getCplColor(row.combinedCpl, combinedGoalCpl);
    });

    return {
      labels,
      datasets: [
        {
          label: "CPL",
          data: values,
          backgroundColor: colors,
          borderRadius: 4,
          barPercentage: 0.75,
          categoryPercentage: 0.75
        }
      ]
    };
  }, [filteredRows, cplView]);

  const cplChartOptions = useMemo(
    () => ({
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        datalabels: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => `CPL: $${ctx.parsed.x.toFixed(2)}`
          }
        }
      },
      scales: {
        x: {
          grid: { color: "#f0f0f0" },
          ticks: { callback: (value) => `$${value}`, font: { family: "Rubik", size: 11 } }
        },
        y: {
          grid: { display: false },
          ticks: { font: { family: "Rubik", size: 11 } }
        }
      }
    }),
    []
  );

  const storeGroups = useMemo(() => {
    const groups = {
      "OEM Division": [],
      "Used Division": [],
      Group: []
    };
    aggregatedRows.forEach((row) => {
      const key = row.divisionGroup === "Used" ? "Used Division" : row.divisionGroup === "Group" ? "Group" : "OEM Division";
      groups[key].push(row.store);
    });
    return groups;
  }, [aggregatedRows]);

  const storeLabel = useMemo(() => {
    if (selectedStores.size === 0) return "None selected";
    if (selectedStores.size === aggregatedRows.length && aggregatedRows.length > 0) return "All Dealerships";
    if (selectedStores.size <= 3) return Array.from(selectedStores).join(", ");
    return `${selectedStores.size} dealerships selected`;
  }, [selectedStores, aggregatedRows.length]);

  const trendSeries = useMemo(() => {
    if (monthlySeries.length === 0) return { labels: [], gads: [], meta: [], combined: [] };

    const labels = monthlySeries.map((month) => month.label);
    const gads = [];
    const meta = [];
    const combined = [];

    monthlySeries.forEach((month) => {
      let gadsSpend = 0;
      let gadsLeads = 0;
      let metaSpend = 0;
      let metaLeads = 0;

      month.stores.forEach((storeRow) => {
        if (selectedStores.size > 0 && !selectedStores.has(storeRow.store)) return;
        if (trendGroup !== "all" && storeRow.divisionGroup.toLowerCase() !== trendGroup) return;
        gadsSpend += storeRow.gadsSpend;
        gadsLeads += storeRow.gadsLeads;
        metaSpend += storeRow.metaSpend;
        metaLeads += storeRow.metaLeads;
      });

      const gadsCpl = gadsLeads ? gadsSpend / gadsLeads : 0;
      const metaCpl = metaLeads ? metaSpend / metaLeads : 0;
      const combinedSpend = gadsSpend + metaSpend;
      const combinedLeads = gadsLeads + metaLeads;
      const combinedCpl = combinedLeads ? combinedSpend / combinedLeads : 0;

      gads.push(Number(gadsCpl.toFixed(2)));
      meta.push(Number(metaCpl.toFixed(2)));
      combined.push(Number(combinedCpl.toFixed(2)));
    });

    return { labels, gads, meta, combined };
  }, [monthlySeries, selectedStores, trendGroup]);

  const trendChartData = useMemo(
    () => ({
      labels: trendSeries.labels,
      datasets: [
        {
          label: "Google",
          data: trendSeries.gads,
          borderColor: "rgb(42, 95, 196)",
          backgroundColor: "rgba(42, 95, 196, 0.2)",
          tension: 0.3,
          pointRadius: 3,
          pointHoverRadius: 4
        },
        {
          label: "Meta",
          data: trendSeries.meta,
          borderColor: "rgb(90, 47, 154)",
          backgroundColor: "rgba(90, 47, 154, 0.2)",
          tension: 0.3,
          pointRadius: 3,
          pointHoverRadius: 4
        },
        {
          label: "Combined",
          data: trendSeries.combined,
          borderColor: "#f6a043",
          backgroundColor: "rgba(246, 160, 67, 0.2)",
          borderDash: [6, 3],
          tension: 0.3,
          pointRadius: 3,
          pointHoverRadius: 4
        }
      ]
    }),
    [trendSeries]
  );

  const trendChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        datalabels: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: $${ctx.parsed.y.toFixed(2)}`
          }
        }
      },
      scales: {
        y: {
          grid: { color: "#f0f0f0" },
          ticks: { callback: (value) => `$${value}`, font: { family: "Rubik", size: 11 } }
        },
        x: {
          grid: { display: false },
          ticks: { font: { family: "Rubik", size: 11 } }
        }
      }
    }),
    []
  );

  const momChartData = useMemo(() => {
    if (monthlySeries.length < 2) return { labels: [], values: [], colors: [] };

    const currentMonth = monthlySeries[monthlySeries.length - 1];
    const prevMonth = monthlySeries[monthlySeries.length - 2];
    const labels = [];
    const values = [];
    const colors = [];

    currentMonth.stores.forEach((storeRow, storeName) => {
      if (selectedStores.size > 0 && !selectedStores.has(storeName)) return;
      const prevRow = prevMonth.stores.get(storeName);
      if (!prevRow) return;

      const currentCpl =
        momView === "google"
          ? storeRow.gadsLeads
            ? storeRow.gadsSpend / storeRow.gadsLeads
            : 0
          : momView === "meta"
          ? storeRow.metaLeads
            ? storeRow.metaSpend / storeRow.metaLeads
            : 0
          : storeRow.gadsLeads + storeRow.metaLeads
          ? (storeRow.gadsSpend + storeRow.metaSpend) /
            (storeRow.gadsLeads + storeRow.metaLeads)
          : 0;

      const prevCpl =
        momView === "google"
          ? prevRow.gadsLeads
            ? prevRow.gadsSpend / prevRow.gadsLeads
            : 0
          : momView === "meta"
          ? prevRow.metaLeads
            ? prevRow.metaSpend / prevRow.metaLeads
            : 0
          : prevRow.gadsLeads + prevRow.metaLeads
          ? (prevRow.gadsSpend + prevRow.metaSpend) /
            (prevRow.gadsLeads + prevRow.metaLeads)
          : 0;

      if (!prevCpl) return;
      const change = ((currentCpl - prevCpl) / prevCpl) * 100;
      labels.push(storeName);
      values.push(Number(change.toFixed(1)));
      colors.push(change <= 0 ? "#57a56d" : "#f26a6f");
    });

    return { labels, values, colors };
  }, [monthlySeries, selectedStores, momView]);

  const momChartDataSet = useMemo(
    () => ({
      labels: momChartData.labels,
      datasets: [
        {
          label: "MoM CPL Change",
          data: momChartData.values,
          backgroundColor: momChartData.colors,
          borderRadius: 4,
          barPercentage: 0.75,
          categoryPercentage: 0.75
        }
      ]
    }),
    [momChartData]
  );

  const momChartOptions = useMemo(
    () => ({
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        datalabels: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.parsed.x.toFixed(1)}%`
          }
        }
      },
      scales: {
        x: {
          grid: { color: "#f0f0f0" },
          ticks: { callback: (value) => `${value}%`, font: { family: "Rubik", size: 11 } }
        },
        y: {
          grid: { display: false },
          ticks: { font: { family: "Rubik", size: 11 } }
        }
      }
    }),
    []
  );

  useEffect(() => {
    if (!storeMenuOpen) return undefined;
    const handleOutside = (event) => {
      if (storeMenuRef.current && !storeMenuRef.current.contains(event.target)) {
        setStoreMenuOpen(false);
      }
    };
    document.addEventListener("click", handleOutside);
    return () => document.removeEventListener("click", handleOutside);
  }, [storeMenuOpen]);

  return (
    <div className="combined-dashboard">
      <header className="combined-header">
        <h1>
          <span>FFUN</span> | Paid Media Combined Performance
        </h1>
        <div className="combined-header-meta">
          <div>{dateLabel}</div>
          <div className="combined-pill-row">
            <span className="combined-pill gads">Google Ads</span>
            <span className="combined-pill meta">Meta Ads</span>
            <span className="combined-pill combined">Combined</span>
          </div>
        </div>
      </header>

      <div className="combined-container">
        <div className="combined-legend">
          <span className="legend-item">
            <span className="legend-dot green" /> CPL at or below goal
          </span>
          <span className="legend-item">
            <span className="legend-dot yellow" /> CPL up to 15% over goal
          </span>
          <span className="legend-item">
            <span className="legend-dot red" /> CPL more than 15% over goal
          </span>
        </div>
        <div className="combined-controls">
          <label>Dealerships:</label>
          <div className="combined-dropdown" ref={storeMenuRef}>
            <button
              type="button"
              className="combined-dropdown-btn"
              onClick={() => setStoreMenuOpen((prev) => !prev)}
            >
              {storeLabel}
            </button>
            <div className={`combined-dropdown-menu ${storeMenuOpen ? "open" : ""}`}>
              <div className="combined-dropdown-actions">
                <button
                  type="button"
                  onClick={() => setSelectedStores(new Set(aggregatedRows.map((row) => row.store)))}
                >
                  Select All
                </button>
                <button type="button" onClick={() => setSelectedStores(new Set())}>
                  Clear
                </button>
              </div>
              <div className="combined-dropdown-divider" />
              <div className="combined-dropdown-section">OEM Division</div>
              {storeGroups["OEM Division"].map((name) => {
                const checked = selectedStores.has(name);
                return (
                  <label className="combined-dropdown-item" key={`oem-${name}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        const next = new Set(selectedStores);
                        if (checked) {
                          next.delete(name);
                        } else {
                          next.add(name);
                        }
                        setSelectedStores(next);
                      }}
                    />
                    {name}
                  </label>
                );
              })}
              <div className="combined-dropdown-divider" />
              <div className="combined-dropdown-section">Used Division</div>
              {storeGroups["Used Division"].map((name) => {
                const checked = selectedStores.has(name);
                return (
                  <label className="combined-dropdown-item" key={`used-${name}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        const next = new Set(selectedStores);
                        if (checked) {
                          next.delete(name);
                        } else {
                          next.add(name);
                        }
                        setSelectedStores(next);
                      }}
                    />
                    {name}
                  </label>
                );
              })}
              <div className="combined-dropdown-divider" />
              <div className="combined-dropdown-section">Group</div>
              {storeGroups["Group"].map((name) => {
                const checked = selectedStores.has(name);
                return (
                  <label className="combined-dropdown-item" key={`group-${name}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        const next = new Set(selectedStores);
                        if (checked) {
                          next.delete(name);
                        } else {
                          next.add(name);
                        }
                        setSelectedStores(next);
                      }}
                    />
                    {name}
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        {error && <div className="combined-error">{error}</div>}
        {loading && <div className="combined-loading">Loading combined performance...</div>}

        <div className="combined-table-wrap">
          <table>
            <thead>
              <tr className="combined-header-row">
                <th rowSpan={2} className="combined-dealership">Dealership</th>
                <th colSpan={GADS_COLUMNS.length} className="combined-group-title google">Google Ads</th>
                <th colSpan={META_COLUMNS.length} className="combined-group-title meta">Meta Ads</th>
                <th colSpan={COMBINED_COLUMNS.length} className="combined-group-title combined">Combined</th>
              </tr>
              <tr className="combined-subheader-row">
                {COLUMNS.filter((col) => col.key !== "name").map((col) => {
                  const boundaryClass =
                    col.key === "gadsSpend"
                      ? "boundary-left"
                      : col.key === "metaSpend"
                      ? "boundary-meta"
                      : col.key === "combinedSpend"
                      ? "boundary-combined"
                      : "";
                  return (
                    <th key={col.key} className={boundaryClass}>
                      {col.label}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 && !loading ? (
                <tr>
                  <td colSpan={COLUMNS.length} className="combined-empty">
                    No combined data found.
                  </td>
                </tr>
              ) : (
                sections.map((section) => (
                  <React.Fragment key={section.key}>
                    {section.rows.length > 0 && (
                      <tr className="combined-division">
                        <td colSpan={COLUMNS.length}>{section.label}</td>
                      </tr>
                    )}
                    {section.rows.map((row) => (
                      <tr key={row.store}>
                        <td className="combined-dealership">{row.store}</td>
                        {COLUMNS.filter((col) => col.key !== "name").map((col) => {
                          let display = row[col.key];
                          if (col.key === "gadsSpend") display = formatCurrency(row.gadsSpend);
                          if (col.key === "gadsLeads") display = formatNumberOne(row.gadsLeads);
                          if (col.key === "gadsCpl") display = formatCurrency(row.gadsCpl);
                          if (col.key === "metaSpend") display = formatCurrency(row.metaSpend);
                          if (col.key === "metaLeads") display = formatNumberOne(row.metaLeads);
                          if (col.key === "metaCpl") display = formatCurrency(row.metaCpl);
                          if (col.key === "combinedSpend") display = formatCurrency(row.combinedSpend);
                          if (col.key === "combinedLeads") display = formatNumberOne(row.combinedLeads);
                          if (col.key === "combinedCpl") display = formatCurrency(row.combinedCpl);

                          const gadsGoalCpl = row.gadsLeadGoal ? row.gadsBudget / row.gadsLeadGoal : 0;
                          const metaGoalCpl = row.metaLeadGoal ? row.metaBudget / row.metaLeadGoal : 0;
                          const combinedGoal = row.gadsLeadGoal + row.metaLeadGoal;
                          const combinedGoalCpl = combinedGoal
                            ? (row.gadsBudget + row.metaBudget) / combinedGoal
                            : 0;

                          const cplClass =
                            col.key === "gadsCpl"
                              ? getCplClass(row.gadsCpl, gadsGoalCpl)
                              : col.key === "metaCpl"
                              ? getCplClass(row.metaCpl, metaGoalCpl)
                              : col.key === "combinedCpl"
                              ? getCplClass(row.combinedCpl, combinedGoalCpl)
                              : "";

                          const boundaryClass =
                            col.key === "gadsSpend"
                              ? "boundary-left"
                              : col.key === "metaSpend"
                              ? "boundary-meta"
                              : col.key === "combinedSpend"
                              ? "boundary-combined"
                              : "";

                          return (
                            <td key={col.key} className={`${boundaryClass} ${cplClass}`.trim()}>
                              {display}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                    {section.rows.length > 0 && (() => {
                      // Compute totals once per section to avoid repeating the reduction per cell.
                      const totals = calculateTotals(section.rows);

                      return (
                        <tr className="combined-total">
                          <td className="combined-dealership">{section.label} Total</td>
                          {COLUMNS.filter((col) => col.key !== "name").map((col) => {
                            let display = totals[col.key];
                            if (col.key === "gadsSpend") display = formatCurrency(totals.gadsSpend);
                            if (col.key === "gadsLeads") display = formatNumberOne(totals.gadsLeads);
                            if (col.key === "gadsCpl") display = formatCurrency(totals.gadsCpl);
                            if (col.key === "metaSpend") display = formatCurrency(totals.metaSpend);
                            if (col.key === "metaLeads") display = formatNumberOne(totals.metaLeads);
                            if (col.key === "metaCpl") display = formatCurrency(totals.metaCpl);
                            if (col.key === "combinedSpend") display = formatCurrency(totals.combinedSpend);
                            if (col.key === "combinedLeads") display = formatNumberOne(totals.combinedLeads);
                            if (col.key === "combinedCpl") display = formatCurrency(totals.combinedCpl);

                            const gadsGoalCpl = totals.gadsLeadGoal
                              ? totals.gadsBudget / totals.gadsLeadGoal
                              : 0;
                            const metaGoalCpl = totals.metaLeadGoal
                              ? totals.metaBudget / totals.metaLeadGoal
                              : 0;
                            const combinedGoal = totals.gadsLeadGoal + totals.metaLeadGoal;
                            const combinedGoalCpl = combinedGoal
                              ? (totals.gadsBudget + totals.metaBudget) / combinedGoal
                              : 0;

                            const cplClass =
                              col.key === "gadsCpl"
                                ? getCplClass(totals.gadsCpl, gadsGoalCpl)
                                : col.key === "metaCpl"
                                ? getCplClass(totals.metaCpl, metaGoalCpl)
                                : col.key === "combinedCpl"
                                ? getCplClass(totals.combinedCpl, combinedGoalCpl)
                                : "";

                            const boundaryClass =
                              col.key === "gadsSpend"
                                ? "boundary-left"
                                : col.key === "metaSpend"
                                ? "boundary-meta"
                                : col.key === "combinedSpend"
                                ? "boundary-combined"
                                : "";

                            return (
                              <td key={col.key} className={`${boundaryClass} ${cplClass}`.trim()}>
                                {display}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })()}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="combined-mid-charts">
          <div className="combined-chart-card">
            <div className="combined-chart-header">
              <div>
                <h3>CPL Trend</h3>
                <p>Last 12 months</p>
              </div>
              <div className="combined-toggle">
                <button
                  type="button"
                  className={trendGroup === "all" ? "active" : ""}
                  onClick={() => setTrendGroup("all")}
                >
                  All
                </button>
                <button
                  type="button"
                  className={trendGroup === "oem" ? "active" : ""}
                  onClick={() => setTrendGroup("oem")}
                >
                  OEM
                </button>
                <button
                  type="button"
                  className={trendGroup === "used" ? "active" : ""}
                  onClick={() => setTrendGroup("used")}
                >
                  Used
                </button>
              </div>
            </div>
            <div className="combined-chart-container">
              <Line data={trendChartData} options={trendChartOptions} />
            </div>
            <div className="combined-chart-legend">
              <span className="legend-dot outline" style={{ color: "rgb(42, 95, 196)" }} />
              Google
              <span className="legend-dot outline" style={{ color: "rgb(90, 47, 154)" }} />
              Meta
              <span className="legend-dot outline" style={{ color: "#f6a043" }} />
              Combined
            </div>
          </div>

          <div className="combined-chart-card">
            <div className="combined-chart-header">
              <div>
                <h3>MoM CPL Change</h3>
                <p>{startDate.format("MMM")} MTD vs {startDate.clone().subtract(1, "month").format("MMM")} EOM</p>
              </div>
              <div className="combined-toggle">
                <button
                  type="button"
                  className={momView === "combined" ? "active" : ""}
                  onClick={() => setMomView("combined")}
                >
                  Combined
                </button>
                <button
                  type="button"
                  className={momView === "google" ? "active" : ""}
                  onClick={() => setMomView("google")}
                >
                  Google
                </button>
                <button
                  type="button"
                  className={momView === "meta" ? "active" : ""}
                  onClick={() => setMomView("meta")}
                >
                  Meta
                </button>
              </div>
            </div>
            <div className="combined-chart-container">
              <Bar data={momChartDataSet} options={momChartOptions} />
            </div>
          </div>
        </div>

        <div className="combined-charts">
          <div className="combined-chart-card">
            <div className="combined-chart-header">
              <div>
                <h3>Leads Goal vs Projected</h3>
                <p>Combined channels - Projected = Total Budget / Current CPL</p>
              </div>
            </div>
            <div className="combined-chart-container">
              <Bar data={leadsChartData} options={leadsChartOptions} />
            </div>
          </div>

          <div className="combined-chart-card">
            <div className="combined-chart-header">
              <div>
                <h3>CPL by Dealership</h3>
                <p>{startDate.format("MMMM")} MTD</p>
              </div>
              <div className="combined-toggle">
                <button
                  type="button"
                  className={cplView === "combined" ? "active" : ""}
                  onClick={() => setCplView("combined")}
                >
                  Combined
                </button>
                <button
                  type="button"
                  className={cplView === "google" ? "active" : ""}
                  onClick={() => setCplView("google")}
                >
                  Google
                </button>
                <button
                  type="button"
                  className={cplView === "meta" ? "active" : ""}
                  onClick={() => setCplView("meta")}
                >
                  Meta
                </button>
              </div>
            </div>
            <div className="combined-chart-container">
              <Bar data={cplChartData} options={cplChartOptions} />
            </div>
          </div>
        </div>

        <div className="combined-footer">
          Source: Google Ads API + Meta Marketing API | Date range {dateLabel}
        </div>
      </div>
    </div>
  );
}
